import React from 'react';
import { Flex, Text, Tooltip } from '@mantine/core';
import { useIdeaFilters } from '../../hooks/useIdeaFilters';
import { getOrgExtraInfoFieldsFromFiltersConfig, ORG_EXTRA_INFO_FIELDS_LIMIT_DEFAULT } from '../../utils/org-filter-config';
import { resolveOrgFieldFromDbPath } from '../../utils/company-filter-label';

// ── Highlight ─────────────────────────────────────────────────────────────────

export function Highlight({ text, term }: { text: string; term?: string }) {
  if (!term || !text) return <>{text || '—'}</>;
  const idx = text.toLowerCase().indexOf(term.toLowerCase());
  if (idx === -1) return <>{text}</>;
  return (
    <>
      {text.slice(0, idx)}
      <mark style={{ background: '#fef08a', borderRadius: 2, padding: '0 1px' }}>
        {text.slice(idx, idx + term.length)}
      </mark>
      {text.slice(idx + term.length)}
    </>
  );
}

// ── component ─────────────────────────────────────────────────────────────────

interface AccountSearchFieldsProps {
  item: any;
  /** Optional search term — when provided, matching text is highlighted. */
  term?: string;
}

/**
 * Renders the company attribute row for an account search result, sourced
 * from the same filters->company->extraUI config used everywhere else a
 * company's extra attributes are shown (falls back to CRM ID only when
 * nothing is configured), so this matches C360, the filter UI, and the
 * Explore agent instead of its own hardcoded field list.
 */
export function AccountSearchFields({ item, term }: AccountSearchFieldsProps) {
  const { ideasFiltersConfig } = useIdeaFilters();
  const orgExtraInfoFields = React.useMemo(
    () => getOrgExtraInfoFieldsFromFiltersConfig(ideasFiltersConfig?.evidence, ORG_EXTRA_INFO_FIELDS_LIMIT_DEFAULT),
    [ideasFiltersConfig?.evidence]
  );

  const fields: { label: string; value: string }[] = React.useMemo(() => {
    // Search-match metadata is independent of the company attribute config, so it is always appended.
    const matchedFields = [
      ...(item?.matchedByCxUserName ? [{ label: 'User Name', value: String(item.matchedByCxUserName) }] : []),
      ...(item?.matchedByCxUserEmail ? [{ label: 'Email',    value: String(item.matchedByCxUserEmail) }] : []),
    ];

    const attributeFields = orgExtraInfoFields
      .map((field) => {
        const rawValue = resolveOrgFieldFromDbPath(item, field.dbFieldPath);
        let value = '';
        if (rawValue !== null && rawValue !== undefined && rawValue !== '') {
          if (typeof rawValue === 'number') value = rawValue.toLocaleString();
          else if (typeof rawValue === 'boolean') value = rawValue ? 'Yes' : 'No';
          else if (typeof rawValue !== 'object') value = String(rawValue);
        }
        return { label: field.tooltip || field.dbFieldPath, value };
      })
      .filter((f) => f.value !== '');

    return [...attributeFields, ...matchedFields];
  }, [item, orgExtraInfoFields]);

  if (fields.length === 0) return null;

  return (
    <Flex wrap="wrap" style={{ gap: '2px 15px' }}>
      {fields.map((f) => (
        <Flex key={f.label} style={{ flex: '0 0 auto' }}>
          <Text c="dimmed" fz="xs" mr="4px" style={{ flexShrink: 0 }}>
            {f.label}:
          </Text>
          <Tooltip label={f.value} multiline withinPortal>
            <Text fz="xs" tt="capitalize">
              <Highlight text={f.value} term={term} />
            </Text>
          </Tooltip>
        </Flex>
      ))}
    </Flex>
  );
}
